/**
 * Função para buscar taxas de câmbio de uma API
 * Esta função pode ser integrada com serviços como:
 * - Open Exchange Rates
 * - Fixer.io
 * - Banco Central do Brasil
 */

import { ExchangeRates, DEFAULT_RATES } from "./useI18n";
import { apiFetch } from "../apiBase";

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas

export async function fetchExchangeRates(): Promise<ExchangeRates> {
  try {
    // Tentar buscar do backend (se implementado)
    const response = await apiFetch("/api/exchange-rates", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn("Failed to fetch exchange rates from backend:", error);
  }
  
  // Fallback para taxas padrão
  return DEFAULT_RATES;
}

export function getCachedExchangeRates(): ExchangeRates | null {
  const cached = localStorage.getItem("rocketanks-exchange-rates");
  if (!cached) return null;
  
  try {
    const data = JSON.parse(cached);
    const lastUpdated = new Date(data.lastUpdated).getTime();
    const now = Date.now();
    
    // Retornar se ainda válido
    if (now - lastUpdated < CACHE_DURATION) {
      return data;
    }
  } catch (error) {
    console.warn("Failed to parse cached exchange rates:", error);
  }
  
  return null;
}

export function cacheExchangeRates(rates: ExchangeRates): void {
  try {
    localStorage.setItem("rocketanks-exchange-rates", JSON.stringify(rates));
  } catch (error) {
    console.warn("Failed to cache exchange rates:", error);
  }
}

export async function initializeI18n() {
  // Tentar usar cache primeiro
  const cached = getCachedExchangeRates();
  if (cached) {
    return cached;
  }
  
  // Buscar novas taxas
  const rates = await fetchExchangeRates();
  cacheExchangeRates(rates);
  
  return rates;
}
